/**
 * 健康度統計カスタムフック
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business プロジェクト群の健康度分布（S/A/B/C/D）と平均スコアを集計。加重平均健康度の算出も提供
 * @graph-connects toGrade [calls] スコアからグレードへの変換
 */

import { useMemo } from 'react';
import { toGrade } from '../../../types/project.js';
import type { Project, HealthScore, HealthGrade } from '../../../types/project.js';

interface HealthStats {
  healthy: number;
  warning: number;
  danger: number;
  noData: number;
  avgScore: number;
}

/** Gen1統合プロダクトの加重平均健康度を算出 */
/** @graph-connects none */
export function calcWeightedHealth(chunks: Project[]): HealthScore | null {
  let totalWeight = 0;
  let weightedScore = 0;
  let weightedProgress = 0;
  let weightedPace = 0;
  let weightedBuffer = 0;

  for (const p of chunks) {
    if (!p.health || p.totalPlannedPoint <= 0) continue;
    const w = p.totalPlannedPoint;
    totalWeight += w;
    weightedScore += p.health.score * w;
    weightedProgress += p.health.progressScore * w;
    weightedPace += p.health.paceScore * w;
    weightedBuffer += p.health.bufferScore * w;
  }

  if (totalWeight <= 0) return null;

  const score = Math.round(weightedScore / totalWeight);
  const grade: HealthGrade = toGrade(score);

  return {
    score,
    grade,
    progressScore: Math.round(weightedProgress / totalWeight),
    paceScore: Math.round(weightedPace / totalWeight),
    bufferScore: Math.round(weightedBuffer / totalWeight),
    spi: null,
    paceRatio: null,
    bufferRate: null,
  };
}

/** 健康度分布の集計フック */
/** @graph-connects none */
export function useHealthStats(projects: Project[]): HealthStats {
  return useMemo(() => {
    let healthy = 0;
    let warning = 0;
    let danger = 0;
    let noData = 0;
    let totalScore = 0;
    let scoredCount = 0;
    for (const p of projects) {
      const g = p.health?.grade;
      if (g === 'S' || g === 'A') healthy++;
      else if (g === 'B') warning++;
      else if (g === 'C' || g === 'D') danger++;
      else noData++;
      if (p.health) {
        totalScore += p.health.score;
        scoredCount++;
      }
    }
    return {
      healthy,
      warning,
      danger,
      noData,
      avgScore: scoredCount > 0 ? Math.round(totalScore / scoredCount) : 0,
    };
  }, [projects]);
}
